// The plan panel, on mobile: a closed strip that opens a bottom sheet holding the plan's
// objective, its roster, and its decisions (`Decisions.tsx`). `DecisionsPanel.tsx` is this
// component's desktop sibling — see its header comment for why they're split. The closed
// strip itself still names only the decision count, because that is the one fact worth
// surfacing before a reader has chosen to open the sheet at all.
//
// This has to be handed to `Conversation.tsx`/`PrivateChat.tsx` as a prop and rendered
// INSIDE their own `.conversation__foot`, directly above the composer, rather than living
// as a `.planBody` sibling the way the desktop panel does. A `.planBody` sibling is laid
// out BESIDE the conversation column, not above its composer, at any flex-direction; the
// only way to actually put this element above the composer in the rendered page is to put
// it above the composer in the document, and the composer is owned by those two
// components. CSS (`.decisionsSheet`, `styles.css`) hides this on desktop, where
// `DecisionsPanel` stands in for it instead.
//
// No `position: fixed` anywhere here (forbidden in `styles.css`, see its top-of-file
// comment): the scrim and sheet are `position: absolute` against `#root`, the same
// viewport-safe technique `.accountMenu__panel` and the operator debug panel use.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { Internals } from '../internals.js';
import type { PlanState } from '../plan/usePlan.js';
import { Decisions } from '../surfaces/Decisions.js';

export type DecisionsMobileStripProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
};

export function DecisionsMobileStrip({
  state,
  internals,
  client,
  planId,
}: DecisionsMobileStripProps): JSX.Element | null {
  const [open, setOpen] = useState(false);

  // Escape closes the sheet, matching every other dismissable surface in this package
  // (`AccountMenu`). Ahead of the `snapshot === null` check below, since that is an early
  // return and every hook here has to run on every render regardless.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const snapshot = state.snapshot;
  if (snapshot === null) {
    return null;
  }

  const openCount = snapshot.decisions.filter(
    (decision) => decision.state !== 'done',
  ).length;

  return (
    <div className="decisionsSheet">
      <button
        type="button"
        className="decisionsSheet__strip"
        onClick={() => setOpen(true)}
      >
        {openCount} open decision{openCount === 1 ? '' : 's'} ›
      </button>
      {open ? (
        <div className="decisionsSheet__scrim" onClick={() => setOpen(false)}>
          <div
            className="decisionsSheet__sheet"
            role="dialog"
            aria-label="Plan"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="decisionsSheet__handle" aria-hidden="true" />
            <div className="decisionsPanel__head">
              <button
                type="button"
                className="iconButton"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            <Decisions
              state={state}
              internals={internals}
              client={client}
              planId={planId}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
